const flow = require('lodash/fp/flow');
const merge = require('lodash/merge');
const curry = require('lodash/fp/curry');
const fill = require('lodash/fill');
const parseRequestMetaData = require('../../utils/parse-request-meta-data');

const padArray = curry((length, padWith, arr) => {
  if (arr.length >= length) {
    return arr;
  }

  return arr.concat(fill(new Array(length - arr.length), padWith));
});

function getQS(parsedMetaData, posId) {
  return {
    userId: parsedMetaData.qs.userId,
    pfId: parsedMetaData.portfolio.pfRelId,
    branchCode: parsedMetaData.portfolio.branchCode,
    destinationCurrency: parsedMetaData.qs.destinationCurrency,
    posId
  };
}

/**
 * Assets API Service
 * @param {Object} config - config
 * @param {string} config.baseUrl - Base url with trailing slash e.g. 'https://1.2.3.4:5678/api/equities/'
 * @param {string} config.authToken - Authorization header bearer token
 * @param {string} config.positionsUrl - Url for positions, e.g. 'equities-positions'
 * @param {string} config.transactionsUrl - Url for transactions, e.g. 'equities-transactions'
 * @param {string} config.valueMovementUrl - Url for value movement, e.g. 'equities-value-movement'
 * @param {string} config.requestHandler - Request handler, returning a Promise
 * @returns {Object} - The service object
 */
function apiFactory({
  baseUrl,
  positionsUrl,
  transactionsUrl,
  valueMovementUrl,
  requestHandler,
  assetCategory,
}) {
  const headers = {
    Accept: 'application/json',
    'Cache-Control': 'max-age=30'
  };

  const requestArray = flow(
    requestHandler,
    x => x.catch(e => {
      if (e.statusCode === 404) return [];
      throw e;
    })
  );

  return {
    /**
     * Positions
     * @param {Object} qs - Request query parameters
     * @param {string} qs.pfId - Portfolio Id
     * @param {string} qs.userId - User Id
     * @param {string} qs.branchCode - Branch Code
     * @param {string} [qs.destinationCurrency] - Requested Destination Currency
     * @param {Object} metaData - encoded jwt and JSESSION
     * @returns {Promise} - Resolves with JSON-decoded body
     */
    getPositions: (portfolioId, metaData, posId) => {
      const parsedMetaData = parseRequestMetaData(metaData);
      const reqOpts = { headers, method: 'POST', json: true };
      
      const assetCls = { 'astCls' : assetCategory};
      const body = merge(getQS(parsedMetaData, posId), assetCls);
      
      return requestArray(merge(
        {},
        reqOpts,
        { url: `${baseUrl}${positionsUrl}`, body },
        parsedMetaData
      ));
    },

    /**
     * Transactions
     * @param {Object} qs - Request query parameters
     * @param {string} qs.posId - Position Id
     * @param {string} qs.branchCode - Branch Code
     * @param {string} qs.userId - User Id
     * @param {Object} metaData - encoded jwt and JSESSION
     * @returns {Promise} - Resolves with JSON-decoded body
     */
    getTransactions: (extraQS, metaData) => {
      const parsedMetaData = parseRequestMetaData(metaData);
      const reqOpts = { headers, method: 'POST', json: true };
      const assetCls = { 'astCls' : assetCategory};
      const { posId } = extraQS;
      const body = merge(extraQS, getQS(parsedMetaData, posId), assetCls);

      return requestArray(merge(
        {},
        reqOpts,
        { url: `${baseUrl}${transactionsUrl}`, body },
        parsedMetaData
      ));
    },

    /**
     * Transactions
     * @param {Object} qs - Request query parameters
     * @param {string} qs.psSecNmb - Position Security Number
     * @param {string} qs.userId - User Id
     * @param {string} qs.branchCode - Branch Code
     * @param {string} qs.fromDate - Requested from date ddmmmyyyy
     * @param {string} qs.toDate - Requested to date ddmmmyyyy
     * @param {Object} metaData - encoded jwt and JSESSION
     * @returns {Promise} - Resolves with JSON-decoded body
     */
    getValueMovement: (extraQS, metaData) => {
      const parsedMetaData = parseRequestMetaData(metaData);
      const reqOpts = { headers, method: 'POST', json: true };
      const assetCls = { 'astCls' : assetCategory};
      const body = merge(extraQS, getQS(parsedMetaData, ''), assetCls);

      return requestArray(merge(
        {},
        reqOpts,
        { url: `${baseUrl}${valueMovementUrl}`, body },
        parsedMetaData
      )).then(padArray(5, null));
    },
  };
}

module.exports = apiFactory;
